import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { ChevronDownIcon, SpinnerIcon } from "@/lib/icons";

// Dev defaults for the optional inspection-platform (Congrid) integration.
// Pre-filled so developers can enable the section with one click and submit.
const INSPECTION_DEV_DEFAULTS = {
  inspectionProjectOwnerId: "TqNYIkIg1Q9izEmWz1GHFRYzo3wnITF3",
  existingInspectionProjectId: "cRgQI9TQea1CFjo0AxITPKbAFPZtVYFi",
  inspectionMatrixId: "27625",
  inspectionTemplateOwnerProjectId: "TbXn6WUtuoKBJLzW6H0JvEroaWVW5ccM",
};

import { ProjectType } from "@/types";
import { useProjectInitialisationData } from "./hooks/useProjectInitialisationData";
import { ProjectInitialisationWorkSectionBreakdownDialog } from "./ProjectInitialisationWorkSectionBreakdownDialog";

const PROJECT_TYPE_LABELS: Record<string, string> = {
  [ProjectType.KVR]: "KVR-hanke",
  [ProjectType.KOKONAISURAKKA]: "Kokonaisurakka",
  [ProjectType.PROJEKTIJOHTO]: "Projektijohto",
};

export interface Step1FormData {
  name: string;
  description: string;
  projectType: ProjectType;
  startDate: string;
  objectiveEndDate: string;
  taskTemplateSetId: string | null;
  schedulingProjectId: string | null;
  tyopaallikkoUserId: string | null;
  vastaavaTyonjohtajaUserId: string | null;
  // Inspection platform fields (optional, for testing)
  inspectionProjectOwnerId: string;
  existingInspectionProjectId: string;
  inspectionMatrixId: string;
  inspectionTemplateOwnerProjectId: string;
  inspectionProjectCode: string;
}

/**
 * Whether the init worker should create a fresh inspection project or link an
 * existing one. "new" submits an empty existingInspectionProjectId, which the
 * worker reads as falsy and takes as "create".
 */
type CongridMode = "new" | "existing";

interface ProjectInitialisationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}

export function ProjectInitialisationDialog({
  open,
  onOpenChange,
  onCreated,
}: ProjectInitialisationDialogProps) {
  const { templateSets, schedulingProjects, users, loading } =
    useProjectInitialisationData();

  const [formData, setFormData] = useState<Step1FormData>({
    name: "",
    description: "",
    projectType: ProjectType.KVR,
    startDate: new Date().toISOString().split("T")[0],
    objectiveEndDate: "",
    taskTemplateSetId: null,
    schedulingProjectId: null,
    tyopaallikkoUserId: null,
    vastaavaTyonjohtajaUserId: null,
    inspectionProjectCode: "",
    ...INSPECTION_DEV_DEFAULTS,
  });

  const [step2Open, setStep2Open] = useState(false);
  const [inspectionExpanded, setInspectionExpanded] = useState(false);
  const [congridMode, setCongridMode] = useState<CongridMode>("existing");

  // Survives a round-trip through "Luo uusi" so switching back restores what
  // was typed rather than snapping to the dev default.
  const lastExistingIdRef = useRef(INSPECTION_DEV_DEFAULTS.existingInspectionProjectId);

  const selectCongridMode = (mode: CongridMode) => {
    setCongridMode(mode);
    setFormData((prev) => {
      if (mode === "new") {
        if (prev.existingInspectionProjectId) {
          lastExistingIdRef.current = prev.existingInspectionProjectId;
        }
        return { ...prev, existingInspectionProjectId: "" };
      }
      return { ...prev, existingInspectionProjectId: lastExistingIdRef.current };
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      projectType: ProjectType.KVR,
      startDate: new Date().toISOString().split("T")[0],
      objectiveEndDate: "",
      taskTemplateSetId: null,
      schedulingProjectId: null,
      tyopaallikkoUserId: null,
      vastaavaTyonjohtajaUserId: null,
      inspectionProjectCode: "",
      ...INSPECTION_DEV_DEFAULTS,
    });
    setInspectionExpanded(false);
    setCongridMode("existing");
    lastExistingIdRef.current = INSPECTION_DEV_DEFAULTS.existingInspectionProjectId;
  };

  const handleOpenChange = (val: boolean) => {
    if (!val) resetForm();
    onOpenChange(val);
  };

  const canProceed =
    formData.name.trim() !== "" &&
    formData.taskTemplateSetId !== null &&
    formData.schedulingProjectId !== null &&
    formData.tyopaallikkoUserId !== null &&
    formData.vastaavaTyonjohtajaUserId !== null &&
    // A blank ID under "existing" would silently fall through to "create new".
    (congridMode === "new" || formData.existingInspectionProjectId.trim() !== "");

  const handleJatka = () => {
    if (!canProceed) return;
    onOpenChange(false);
    setStep2Open(true);
  };

  const templateSetOptions = templateSets.map((t) => ({
    value: t.id,
    label: t.name,
    description: t.description ?? undefined,
  }));

  const schedulingProjectOptions = schedulingProjects.map((p) => ({
    value: p.id,
    label: p.name,
    description: p.projectCode ?? undefined,
  }));

  const userOptions = users.map((u) => ({
    value: u.id,
    label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
    description: u.email,
  }));

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="w-screen h-dvh max-w-full max-h-dvh rounded-none flex flex-col overflow-hidden box-border sm:w-full sm:h-auto sm:max-w-[600px] sm:max-h-[90dvh] sm:rounded-lg">
          <DialogHeader className="shrink-0 min-w-0">
            <DialogTitle>Luo uusi hanke</DialogTitle>
            <DialogDescription>
              Täytä hankkeen perustiedot jatkaaksesi projektin alustukseen.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex flex-1 items-center justify-center py-10">
              <SpinnerIcon className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-4 py-4 px-1 flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 touch-pan-y overscroll-contain">
              <div className="grid gap-2 min-w-0">
                <Label htmlFor="init-name">
                  Nimi <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="init-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="esim. Jakomäenpolku 8"
                  className="w-full max-w-full box-border"
                />
              </div>

              <div className="grid gap-2 min-w-0">
                <Label htmlFor="init-description">Kuvaus</Label>
                <Textarea
                  id="init-description"
                  value={formData.description}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    e.target.style.height = "auto";
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  placeholder="Hankkeen kuvaus (valinnainen)"
                  rows={3}
                  className="resize-none overflow-hidden w-full max-w-full box-border"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 min-w-0">
                <div className="grid gap-2 min-w-0">
                  <Label>Hanketyyppi</Label>
                  <Select
                    value={formData.projectType}
                    onValueChange={(v) =>
                      setFormData({ ...formData, projectType: v as ProjectType })
                    }
                  >
                    <SelectTrigger className="w-full min-w-0 overflow-hidden [&>span]:truncate">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(ProjectType).map((pt) => (
                        <SelectItem key={pt} value={pt}>
                          {PROJECT_TYPE_LABELS[pt]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2 min-w-0">
                  <Label htmlFor="init-startDate">Aloituspäivä</Label>
                  <Input
                    id="init-startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full max-w-full box-border"
                  />
                </div>
              </div>

              <div className="grid gap-2 min-w-0">
                <Label htmlFor="init-endDate">Tavoitevalmistuminen</Label>
                <Input
                  id="init-endDate"
                  type="date"
                  value={formData.objectiveEndDate}
                  onChange={(e) =>
                    setFormData({ ...formData, objectiveEndDate: e.target.value })
                  }
                  className="w-full max-w-full box-border"
                />
              </div>

              <div className="grid gap-2 min-w-0">
                <Label>
                  Tehtäväpohja <span className="text-red-500">*</span>
                </Label>
                <Combobox
                  options={templateSetOptions}
                  value={formData.taskTemplateSetId}
                  onChange={(v) => setFormData({ ...formData, taskTemplateSetId: v })}
                  placeholder="Valitse tehtäväpohja..."
                  searchPlaceholder="Hae tehtäväpohjaa..."
                />
              </div>

              <div className="grid gap-2 min-w-0">
                <Label>
                  Sitedrive-aikataulu <span className="text-red-500">*</span>
                </Label>
                <Combobox
                  options={schedulingProjectOptions}
                  value={formData.schedulingProjectId}
                  onChange={(v) => setFormData({ ...formData, schedulingProjectId: v })}
                  placeholder="Valitse Sitedrive-projekti..."
                  searchPlaceholder="Hae projektia..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4 min-w-0">
                <div className="grid gap-2 min-w-0">
                  <Label>
                    Työpäällikkö <span className="text-red-500">*</span>
                  </Label>
                  <Combobox
                    options={userOptions}
                    value={formData.tyopaallikkoUserId}
                    onChange={(v) => setFormData({ ...formData, tyopaallikkoUserId: v })}
                    placeholder="Valitse työpäällikkö..."
                    searchPlaceholder="Hae käyttäjää..."
                  />
                </div>

                <div className="grid gap-2 min-w-0">
                  <Label>
                    Vastaava työnjohtaja <span className="text-red-500">*</span>
                  </Label>
                  <Combobox
                    options={userOptions}
                    value={formData.vastaavaTyonjohtajaUserId}
                    onChange={(v) =>
                      setFormData({ ...formData, vastaavaTyonjohtajaUserId: v })
                    }
                    placeholder="Valitse vastaava tj..."
                    searchPlaceholder="Hae käyttäjää..."
                  />
                </div>
              </div>

              <div className="grid gap-2 min-w-0">
                <Label>Congrid projekti</Label>
                <div className="grid grid-cols-2 gap-4 min-w-0">
                  <Button
                    type="button"
                    variant={congridMode === "new" ? "default" : "outline"}
                    aria-pressed={congridMode === "new"}
                    onClick={() => selectCongridMode("new")}
                    className="w-full min-w-0 font-normal"
                  >
                    <span className="truncate">Luo uusi</span>
                  </Button>
                  <Button
                    type="button"
                    variant={congridMode === "existing" ? "default" : "outline"}
                    aria-pressed={congridMode === "existing"}
                    onClick={() => selectCongridMode("existing")}
                    className="w-full min-w-0 font-normal"
                  >
                    <span className="truncate">Käytä olemassa olevaa</span>
                  </Button>
                </div>

                {congridMode === "new" && (
                  <div className="grid gap-2 min-w-0 mt-2">
                    <Label htmlFor="init-inspectionProjectCode">Projektikoodi</Label>
                    <Input
                      id="init-inspectionProjectCode"
                      value={formData.inspectionProjectCode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inspectionProjectCode: e.target.value,
                        })
                      }
                      placeholder="HXXXX"
                      className="w-1/3 max-w-full box-border"
                    />
                  </div>
                )}

                {congridMode === "existing" && (
                  <div className="grid gap-2 min-w-0 mt-2">
                    <Label htmlFor="init-existingInspectionProjectId">
                      Olemassa oleva tarkastusprojekti ID{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="init-existingInspectionProjectId"
                      value={formData.existingInspectionProjectId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          existingInspectionProjectId: e.target.value,
                        })
                      }
                      placeholder="Käytä olemassa olevaa tarkastusprojektia"
                      className="w-full max-w-full box-border"
                    />
                  </div>
                )}
              </div>

              {/* Inspection platform fields — dev-only, collapsible.
                  Values are pre-filled with dev defaults and active by default. */}
              <div className="border-t pt-4 mt-2">
                <button
                  type="button"
                  onClick={() => setInspectionExpanded((v) => !v)}
                  className="flex items-center gap-2 w-full text-left mb-3"
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      inspectionExpanded ? "" : "-rotate-90"
                    }`}
                  />
                  <span className="text-sm font-medium text-muted-foreground">
                    Tarkastusalustan vakioparametrit
                  </span>
                </button>

                {inspectionExpanded && (
                <div className="grid grid-cols-2 gap-4 min-w-0">
                  <div className="grid gap-2 min-w-0">
                    <Label htmlFor="init-inspectionProjectOwnerId">
                      Tarkastusprojektin omistaja ID
                    </Label>
                    <Input
                      id="init-inspectionProjectOwnerId"
                      value={formData.inspectionProjectOwnerId}
                      onChange={(e) =>
                        setFormData({ ...formData, inspectionProjectOwnerId: e.target.value })
                      }
                      placeholder="Luo uusi tarkastusprojekti tälle omistajalle"
                      className="w-full max-w-full box-border"
                    />
                  </div>

                  <div className="grid gap-2 min-w-0">
                    <Label htmlFor="init-inspectionMatrixId">
                      Lähde-matriisi ID
                    </Label>
                    <Input
                      id="init-inspectionMatrixId"
                      value={formData.inspectionMatrixId}
                      onChange={(e) =>
                        setFormData({ ...formData, inspectionMatrixId: e.target.value })
                      }
                      placeholder="Matriisi josta kopioidaan työvaiheet"
                      className="w-full max-w-full box-border"
                    />
                  </div>

                  <div className="grid gap-2 min-w-0">
                    <Label htmlFor="init-inspectionTemplateOwnerProjectId">
                      Tarkastuspohjien lähdeprojekti ID
                    </Label>
                    <Input
                      id="init-inspectionTemplateOwnerProjectId"
                      value={formData.inspectionTemplateOwnerProjectId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inspectionTemplateOwnerProjectId: e.target.value,
                        })
                      }
                      placeholder="Projekti josta kopioidaan tarkastuspohjat"
                      className="w-full max-w-full box-border"
                    />
                  </div>
                </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Peruuta
            </Button>
            <Button onClick={handleJatka} disabled={!canProceed || loading}>
              Jatka (1/2)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProjectInitialisationWorkSectionBreakdownDialog
        open={step2Open}
        onOpenChange={setStep2Open}
        step1Data={formData}
        onCreated={onCreated}
      />
    </>
  );
}
